import React, { useState, useEffect } from 'react';
import { NavigationScreen, RecordingSession, ScheduledEvent } from '@/types';
import { formatDuration, formatFileSize, formatDate } from '@/utils/helpers';
import { SchedulerService } from '@/services/schedulerService';
import {
  Video,
  Clock,
  HardDrive,
  Users,
  Play,
  ArrowRight,
  ShieldCheck,
  CheckCircle2,
  Sparkles,
  UploadCloud,
  Camera,
  Mic,
  Radio,
  Hash,
  Lock,
  Zap,
  Layers,
  ArrowUpRight,
  Calendar,
  CalendarDays,
  CalendarCheck
} from 'lucide-react';

interface DashboardScreenProps {
  recordings: RecordingSession[];
  onNavigate: (screen: NavigationScreen) => void;
  onSelectRecording: (session: RecordingSession) => void;
  onStartNewRecording: () => void;
  onRecordYourself?: () => void;
  onRecordPodcast?: () => void;
  onStartOnlineMeeting?: () => void;
  onStartGroupMeeting?: (roomCode?: string) => void;
  onImportFile: () => void;
  storageMetrics: {
    totalBytes: number;
    totalDurationSec: number;
    totalParticipants: number;
    totalRecordings: number;
    totalExports: number;
  };
}

export const DashboardScreen: React.FC<DashboardScreenProps> = ({
  recordings,
  onNavigate,
  onSelectRecording,
  onStartNewRecording,
  onRecordYourself,
  onRecordPodcast,
  onStartOnlineMeeting,
  onStartGroupMeeting,
  onImportFile,
  storageMetrics
}) => {
  const [quickRoomCode, setQuickRoomCode] = useState('');
  const [upcomingEvents, setUpcomingEvents] = useState<ScheduledEvent[]>([]);

  useEffect(() => {
    const loadUpcoming = () => {
      try {
        const allEvents = SchedulerService.getEvents();
        const upcoming = allEvents
          .filter(e => new Date(e.endTime).getTime() >= Date.now())
          .slice(0, 3);
        setUpcomingEvents(upcoming);
      } catch {
        // fallback
      }
    };

    loadUpcoming();

    if (typeof window !== 'undefined') {
      window.addEventListener('meetsplit_schedule_changed', loadUpcoming);
      window.addEventListener('storage', loadUpcoming);
      return () => {
        window.removeEventListener('meetsplit_schedule_changed', loadUpcoming);
        window.removeEventListener('storage', loadUpcoming);
      };
    }
  }, []);

  const handleJoinQuickRoom = (e: React.FormEvent) => {
    e.preventDefault();
    const code = quickRoomCode.trim();
    if (onStartGroupMeeting) {
      onStartGroupMeeting(code || undefined);
    } else {
      onNavigate('group-meeting');
    }
  };

  return (
    <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-8 ambient-gradient">
      {/* Top Banner / Hero - Balanced 2-Column Responsive Layout */}
      <div className="relative rounded-3xl p-6 md:p-8 bg-gradient-to-br from-brand-surface via-brand-surface to-brand-elevated border border-brand-border shadow-elevated overflow-hidden">
        {/* Subtle Ambient Decorative Glows */}
        <div className="absolute -right-20 -bottom-20 w-80 h-80 bg-brand-accent/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute left-1/2 -top-20 w-60 h-60 bg-purple-500/10 rounded-full blur-2xl pointer-events-none" />

        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          {/* Left Column (7 cols): Hero Title & Primary CTAs */}
          <div className="lg:col-span-7 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-accent/15 border border-brand-accent/25 text-brand-accent text-xs font-semibold shadow-sm">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Privacy-First Screen & Participant Separation</span>
            </div>

            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-brand-text tracking-tight leading-tight">
              Record Once.{' '}
              <span className="bg-gradient-to-r from-brand-accent via-indigo-500 to-purple-600 bg-clip-text text-transparent">
                Split Every Speaker.
              </span>
            </h1>

            <p className="text-xs sm:text-sm text-brand-muted leading-relaxed max-w-xl">
              Capture meetings, extract individual participant video tiles locally via computer vision,
              and export clean MP4s for creators, educators, and remote teams.
            </p>

            {/* Clean Primary Actions */}
            <div className="flex flex-wrap items-center gap-3 pt-2">
              <button
                onClick={() => onStartGroupMeeting ? onStartGroupMeeting() : onNavigate('group-meeting')}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:brightness-110 text-white text-xs sm:text-sm font-semibold shadow-glow transition-all active:scale-95 cursor-pointer"
              >
                <Users className="w-4 h-4 text-cyan-200" />
                <span>Start Group Meeting</span>
              </button>

              <button
                onClick={onStartNewRecording}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-elevated hover:bg-brand-elevated/80 text-brand-text text-xs sm:text-sm font-semibold border border-brand-border transition-all active:scale-95 cursor-pointer shadow-sm"
              >
                <Video className="w-4 h-4 text-brand-accent" />
                <span>Record Meeting / Screen</span>
              </button>

              <button
                onClick={onImportFile}
                className="flex items-center gap-2 px-3.5 py-2.5 rounded-xl hover:bg-brand-elevated/60 text-brand-muted hover:text-brand-text text-xs sm:text-sm font-medium border border-dashed border-brand-border transition-colors cursor-pointer"
              >
                <UploadCloud className="w-4 h-4 text-brand-secondary" />
                <span>Import Video</span>
              </button>
            </div>
          </div>

          {/* Right Column (5 cols): Instant Room Connect & Privacy Shield */}
          <div className="lg:col-span-5">
            <div className="rounded-2xl p-5 bg-brand-surface/90 border border-brand-border shadow-card backdrop-blur-md space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                  </span>
                  <span className="text-xs font-bold text-brand-text">Instant Room Connect</span>
                </div>
                <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 text-[10px] font-semibold">
                  P2P Online
                </span>
              </div>

              <p className="text-[11px] text-brand-muted leading-relaxed">
                Enter a meeting code or invitation ID to join an active video call directly:
              </p>

              {/* Room Code Quick Join Form */}
              <form onSubmit={handleJoinQuickRoom} className="flex gap-2">
                <div className="relative flex-1">
                  <Hash className="w-4 h-4 text-brand-muted absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    type="text"
                    value={quickRoomCode}
                    onChange={(e) => setQuickRoomCode(e.target.value)}
                    placeholder="e.g. room-alpha-92"
                    className="w-full pl-9 pr-3 py-2 rounded-xl bg-brand-elevated border border-brand-border text-xs text-brand-text placeholder:text-brand-muted/70 focus:outline-none focus:border-brand-accent transition-colors font-mono"
                  />
                </div>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-xl bg-brand-accent hover:bg-brand-accent-hover text-white text-xs font-semibold shadow-sm transition-all flex items-center gap-1 active:scale-95 shrink-0"
                >
                  <span>Join</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </form>

              {/* Security & Privacy Badges */}
              <div className="grid grid-cols-3 gap-2 pt-2 border-t border-brand-border/60 text-center">
                <div className="p-2 rounded-lg bg-brand-elevated/50 flex flex-col items-center">
                  <ShieldCheck className="w-3.5 h-3.5 text-brand-accent mb-1" />
                  <span className="text-[10px] font-medium text-brand-text">100% Offline AI</span>
                  <span className="text-[9px] text-brand-muted">Local GPU</span>
                </div>
                <div className="p-2 rounded-lg bg-brand-elevated/50 flex flex-col items-center">
                  <Lock className="w-3.5 h-3.5 text-purple-400 mb-1" />
                  <span className="text-[10px] font-medium text-brand-text">Direct Mesh</span>
                  <span className="text-[9px] text-brand-muted">Encrypted</span>
                </div>
                <div className="p-2 rounded-lg bg-brand-elevated/50 flex flex-col items-center">
                  <Zap className="w-3.5 h-3.5 text-emerald-400 mb-1" />
                  <span className="text-[10px] font-medium text-brand-text">Zero Latency</span>
                  <span className="text-[9px] text-brand-muted">No Cloud Lag</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Studio Workspaces & Tools - 4-Column Interactive Mode Grid */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-brand-text flex items-center gap-2">
              <Layers className="w-4 h-4 text-brand-accent" />
              <span>Studio Workspaces & Modes</span>
            </h2>
            <p className="text-xs text-brand-muted">Choose your preferred workspace for recording, broadcasting, or calling</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Card 1: Group Meeting */}
          <div
            onClick={() => onStartGroupMeeting ? onStartGroupMeeting() : onNavigate('group-meeting')}
            className="group p-5 rounded-2xl bg-brand-surface hover:bg-brand-elevated border border-brand-border hover:border-indigo-400/50 shadow-card transition-all cursor-pointer flex flex-col justify-between relative overflow-hidden"
          >
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-blue-600 to-indigo-600 flex items-center justify-center text-white shadow-md group-hover:scale-105 transition-transform">
                  <Users className="w-5 h-5 text-cyan-200" />
                </div>
                <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 text-[10px] font-semibold">
                  Multi-User P2P
                </span>
              </div>

              <div>
                <h3 className="font-bold text-sm text-brand-text group-hover:text-indigo-400 transition-colors">
                  Group Meeting
                </h3>
                <p className="text-[11px] text-brand-muted mt-1 leading-relaxed">
                  Multi-participant video calls with live chat, screen share, and single master composite recording.
                </p>
              </div>
            </div>

            <div className="pt-4 border-t border-brand-border/50 mt-4 flex items-center justify-between text-xs text-brand-accent font-semibold">
              <span>Launch Meeting</span>
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </div>
          </div>

          {/* Card 2: Screen & Meeting Capture */}
          <div
            onClick={onStartNewRecording}
            className="group p-5 rounded-2xl bg-brand-surface hover:bg-brand-elevated border border-brand-border hover:border-brand-accent/50 shadow-card transition-all cursor-pointer flex flex-col justify-between relative overflow-hidden"
          >
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-indigo-600 to-brand-accent flex items-center justify-center text-white shadow-md group-hover:scale-105 transition-transform">
                  <Video className="w-5 h-5 text-white" />
                </div>
                <span className="px-2 py-0.5 rounded-full bg-brand-accent/15 border border-brand-accent/25 text-brand-accent text-[10px] font-semibold">
                  AI Split Ready
                </span>
              </div>

              <div>
                <h3 className="font-bold text-sm text-brand-text group-hover:text-brand-accent transition-colors">
                  Screen & App Capture
                </h3>
                <p className="text-[11px] text-brand-muted mt-1 leading-relaxed">
                  Capture entire screen or application. AI computer vision isolates each speaker into separate tracks.
                </p>
              </div>
            </div>

            <div className="pt-4 border-t border-brand-border/50 mt-4 flex items-center justify-between text-xs text-brand-accent font-semibold">
              <span>Record Screen</span>
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </div>
          </div>

          {/* Card 3: Podcast Studio Pro */}
          <div
            onClick={onRecordPodcast || (() => onNavigate('podcast-studio'))}
            className="group p-5 rounded-2xl bg-brand-surface hover:bg-brand-elevated border border-brand-border hover:border-purple-400/50 shadow-card transition-all cursor-pointer flex flex-col justify-between relative overflow-hidden"
          >
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-purple-600 to-pink-600 flex items-center justify-center text-white shadow-md group-hover:scale-105 transition-transform">
                  <Mic className="w-5 h-5 text-white" />
                </div>
                <span className="px-2 py-0.5 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-400 text-[10px] font-semibold">
                  Dual Mic & SFX
                </span>
              </div>

              <div>
                <h3 className="font-bold text-sm text-brand-text group-hover:text-purple-400 transition-colors">
                  Podcast Studio Pro
                </h3>
                <p className="text-[11px] text-brand-muted mt-1 leading-relaxed">
                  Dual-host podcast recording with real-time soundboard effects, audio visualizers, and online guest call.
                </p>
              </div>
            </div>

            <div className="pt-4 border-t border-brand-border/50 mt-4 flex items-center justify-between text-xs text-purple-400 font-semibold">
              <span>Open Podcast</span>
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </div>
          </div>

          {/* Card 4: Solo Studio (Cam & Screen) */}
          <div
            onClick={onRecordYourself || onStartNewRecording}
            className="group p-5 rounded-2xl bg-brand-surface hover:bg-brand-elevated border border-brand-border hover:border-sky-400/50 shadow-card transition-all cursor-pointer flex flex-col justify-between relative overflow-hidden"
          >
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-sky-500 to-indigo-600 flex items-center justify-center text-white shadow-md group-hover:scale-105 transition-transform">
                  <Camera className="w-5 h-5 text-white" />
                </div>
                <span className="px-2 py-0.5 rounded-full bg-sky-500/10 border border-sky-500/20 text-sky-400 text-[10px] font-semibold">
                  Camera + PIP
                </span>
              </div>

              <div>
                <h3 className="font-bold text-sm text-brand-text group-hover:text-sky-400 transition-colors">
                  Record Yourself
                </h3>
                <p className="text-[11px] text-brand-muted mt-1 leading-relaxed">
                  Solo video studio with floating webcam over screen. Ideal for tutorials, presentations, and courses.
                </p>
              </div>
            </div>

            <div className="pt-4 border-t border-brand-border/50 mt-4 flex items-center justify-between text-xs text-sky-400 font-semibold">
              <span>Record Solo</span>
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </div>
          </div>
        </div>
      </div>

      {/* Real Statistics Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 rounded-2xl bg-brand-surface border border-brand-border shadow-card flex items-center gap-3">
          <div className="p-3 rounded-xl bg-brand-accent/15 text-brand-accent border border-brand-accent/30">
            <Video className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[11px] text-brand-muted font-medium">Total Recordings</p>
            <h3 className="text-lg md:text-xl font-bold text-brand-text tabular-nums">
              {storageMetrics.totalRecordings}
            </h3>
          </div>
        </div>

        <div className="p-4 rounded-2xl bg-brand-surface border border-brand-border shadow-card flex items-center gap-3">
          <div className="p-3 rounded-xl bg-brand-secondary/15 text-brand-secondary border border-brand-secondary/30">
            <Clock className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[11px] text-brand-muted font-medium">Recorded Time</p>
            <h3 className="text-lg md:text-xl font-bold text-brand-text tabular-nums font-mono">
              {formatDuration(storageMetrics.totalDurationSec)}
            </h3>
          </div>
        </div>

        <div className="p-4 rounded-2xl bg-brand-surface border border-brand-border shadow-card flex items-center gap-3">
          <div className="p-3 rounded-xl bg-purple-500/15 text-purple-400 border border-purple-500/30">
            <Users className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[11px] text-brand-muted font-medium">Participants Split</p>
            <h3 className="text-lg md:text-xl font-bold text-brand-text tabular-nums">
              {storageMetrics.totalParticipants}
            </h3>
          </div>
        </div>

        <div className="p-4 rounded-2xl bg-brand-surface border border-brand-border shadow-card flex items-center gap-3">
          <div className="p-3 rounded-xl bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
            <HardDrive className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[11px] text-brand-muted font-medium">Local Storage Used</p>
            <h3 className="text-lg md:text-xl font-bold text-brand-text tabular-nums">
              {formatFileSize(storageMetrics.totalBytes)}
            </h3>
          </div>
        </div>
      </div>

      {/* Upcoming Podcasts & Meetings Schedule Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-brand-text flex items-center gap-2">
              <CalendarCheck className="w-4 h-4 text-brand-accent" />
              <span>Upcoming Podcasts & Meetings</span>
              <span className="px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 text-[10px] font-mono font-bold">
                GOOGLE CALENDAR
              </span>
            </h2>
            <p className="text-xs text-brand-muted">Planned sessions synced with Google Calendar</p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => onNavigate('scheduler')}
              className="flex items-center gap-1.5 text-xs font-semibold text-brand-accent hover:text-brand-accent-hover transition-colors cursor-pointer"
            >
              <span>Open Scheduler</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {upcomingEvents.length === 0 ? (
          <div className="p-6 rounded-2xl bg-brand-surface/40 border border-dashed border-brand-border text-center space-y-2">
            <p className="text-xs text-brand-muted">No sessions currently scheduled for today or tomorrow.</p>
            <button
              onClick={() => onNavigate('scheduler')}
              className="px-3 py-1.5 rounded-xl bg-brand-accent text-white text-xs font-semibold shadow-sm hover:brightness-110 cursor-pointer"
            >
              + Schedule Podcast or Meeting
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {upcomingEvents.map(evt => {
              const isPod = evt.type === 'podcast';
              const startDate = new Date(evt.startTime);
              return (
                <div
                  key={evt.id}
                  onClick={() => onNavigate('scheduler')}
                  className="p-4 rounded-2xl bg-brand-surface hover:bg-brand-elevated border border-brand-border hover:border-brand-accent/40 shadow-card transition-all cursor-pointer flex flex-col justify-between space-y-3"
                >
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span
                        className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                          isPod
                            ? 'bg-purple-500/15 text-purple-400 border border-purple-500/25'
                            : 'bg-blue-500/15 text-blue-400 border border-blue-500/25'
                        }`}
                      >
                        {isPod ? <Mic className="w-3 h-3" /> : <Users className="w-3 h-3" />}
                        <span>{isPod ? 'Podcast' : 'Meeting'}</span>
                      </span>

                      <span className="text-[10px] font-mono text-brand-muted">
                        {startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>

                    <h4 className="font-bold text-xs sm:text-sm text-brand-text line-clamp-1">{evt.title}</h4>

                    {evt.description && (
                      <p className="text-[11px] text-brand-muted line-clamp-2 leading-relaxed">
                        {evt.description}
                      </p>
                    )}
                  </div>

                  <div className="pt-2 border-t border-brand-border/40 flex items-center justify-between text-[11px]">
                    <span className="text-brand-muted font-mono">
                      {startDate.toLocaleDateString([], { month: 'short', day: 'numeric' })}
                    </span>
                    <span className="font-semibold text-brand-accent flex items-center gap-1">
                      <span>View</span>
                      <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent Recordings Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-brand-text">Recent Recordings</h2>
            <p className="text-xs text-brand-muted">Recorded sessions and ready participant packages</p>
          </div>

          <button
            onClick={() => onNavigate('library')}
            className="flex items-center gap-1.5 text-xs font-semibold text-brand-accent hover:text-brand-accent-hover transition-colors"
          >
            <span>View All Library</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>

        {recordings.length === 0 ? (
          <div className="p-12 rounded-2xl bg-brand-surface/40 border border-dashed border-brand-border text-center space-y-3">
            <Video className="w-10 h-10 text-brand-muted mx-auto" />
            <h3 className="text-sm font-semibold text-brand-text">No recordings yet</h3>
            <p className="text-xs text-brand-muted max-w-sm mx-auto">
              Launch your meeting, click "Start New Recording", and MeetSplit AI will capture and separate your participants locally.
            </p>
            <button
              onClick={onStartNewRecording}
              className="px-4 py-2 rounded-xl bg-brand-accent hover:bg-brand-accent-hover text-white text-xs font-semibold shadow-glow transition-all cursor-pointer"
            >
              Start First Recording
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {recordings.slice(0, 6).map((rec) => (
              <div
                key={rec.id}
                onClick={() => {
                  onSelectRecording(rec);
                  onNavigate('recording-details');
                }}
                className="group p-4 rounded-2xl bg-brand-surface hover:bg-brand-elevated border border-brand-border hover:border-brand-accent/40 shadow-card transition-all cursor-pointer flex flex-col justify-between"
              >
                <div>
                  {/* Thumbnail / Mock Preview */}
                  <div className="w-full aspect-video rounded-xl bg-brand-elevated relative overflow-hidden mb-3 border border-brand-border/60 flex items-center justify-center">
                    {rec.thumbnailUrl ? (
                      <img src={rec.thumbnailUrl} alt={rec.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="flex flex-col items-center justify-center text-brand-muted/70">
                        <Video className="w-8 h-8 text-brand-accent/50 group-hover:scale-110 transition-transform" />
                        <span className="text-[10px] mt-1 font-mono">{rec.resolution.width}x{rec.resolution.height}</span>
                      </div>
                    )}

                    {/* Duration badge */}
                    <span className="absolute bottom-2 right-2 px-1.5 py-0.5 rounded-md bg-black/80 font-mono text-[10px] text-white">
                      {formatDuration(rec.durationSec)}
                    </span>

                    {/* Status badge */}
                    <span className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-emerald-500/20 border border-emerald-500/30 text-[10px] font-semibold text-emerald-300">
                      Ready
                    </span>
                  </div>

                  <h3 className="font-semibold text-xs md:text-sm text-brand-text group-hover:text-brand-accent transition-colors line-clamp-1">
                    {rec.title}
                  </h3>

                  <p className="text-[11px] text-brand-muted mt-1">
                    {formatDate(rec.createdAt)} • {formatFileSize(rec.fileSize)}
                  </p>
                </div>

                {/* Participant badges */}
                <div className="pt-4 border-t border-brand-border/50 mt-4 flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-xs text-brand-muted">
                    <Users className="w-3.5 h-3.5 text-brand-secondary" />
                    <span>{rec.participants?.length || 0} participants</span>
                  </div>

                  <div className="flex items-center gap-1 text-xs text-brand-accent font-medium group-hover:translate-x-1 transition-transform">
                    <span>Details</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
